import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { OrderInfo, OrderLogTableEntry } from 'components/Orders/Info/type'
import { GET_ORDER_STATUS_LOG } from 'components/Orders/OrderQueries'
import { Table } from 'antd'
import { composeStatusLog } from './utils'
import styled from '@emotion/styled'

interface StatusLogProps {
  order: OrderInfo
  reloadOrder: () => void
}

const StatusLog = ({ order }: StatusLogProps) => {
  const [statusLog, setStatusLog] = useState<OrderLogTableEntry[]>([])

  const { loading } = useQuery(GET_ORDER_STATUS_LOG, {
    variables: { transactionId: order.transaction_id },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setStatusLog(composeStatusLog(data?.orderStatusLogs))
    }
  })

  const columns = [
    {
      title: '',
      key: 'id',
      render: (_text: string, _record: OrderLogTableEntry, index: number) => (
        <Point isFirst={index === 0} />
      )
    },
    {
      title: <Text bold>Time</Text>,
      dataIndex: 'time',
      key: 'time',
      render: (time: string, record: OrderLogTableEntry) => (
        <TextWrapper>
          <Text>{time}</Text>
          <Description>{record.date}</Description>
        </TextWrapper>
      )
    },
    {
      title: <Text bold>Event</Text>,
      dataIndex: 'event',
      key: 'event',
      render: (event: string, record: OrderLogTableEntry) => (
        <TextWrapper>
          <Text>{event}</Text>
          {!!record.url && !!record.description && (
            <Link href={record.url} target='_blank' rel='noopener noreferrer'>
              {record.description}
            </Link>
          )}
          {!!record.description && !record.url && (
            <Description>{record.description}</Description>
          )}
        </TextWrapper>
      ),
      width: '55%'
    },
    {
      title: (
        <TextWrapper align='end'>
          <Text bold>Performed by</Text>
        </TextWrapper>
      ),
      dataIndex: 'responsible',
      key: 'responsible',
      render: (responsible: string) => (
        <TextWrapper align='end'>
          <Text>{responsible}</Text>
        </TextWrapper>
      )
    }
  ]

  return (
    <TableWrapper>
      <Table
        data-testid='additional-charges-table'
        dataSource={statusLog}
        columns={columns}
        pagination={false}
        loading={loading}
        rowKey='id'
      />
    </TableWrapper>
  )
}

const Link = styled.a`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
  text-decoration: underline;
  cursor: pointer;

  &:visited,
  &:hover,
  &:active,
  &:focus {
    text-decoration: underline;
  }
`

const TextWrapper = styled.div<{ align?: 'start' | 'end' }>(
  ({ align }) => `
  display: flex;
  flex-direction: column;
  align-items: ${align || 'start'};
  line-height: 1;
  gap: 4px;
`
)

const Text = styled.span<{ bold?: boolean }>(
  ({ bold }) => `
  font-size: 14px;
  font-weight: ${bold ? '600' : '400'};
  color: #4a4a68;
  text-transform: capitalize;
`
)

const Description = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: #77778e;
`

const TableWrapper = styled.div`
  .ant-table-tbody td.ant-table-cell {
    height: 56px !important;
    border-bottom: none !important;
    padding: 8px 16px !important;
  }

  .ant-table-thead tr {
    border-bottom: 1px solid #f3f2f4 !important;

    th.ant-table-cell {
      height: 56px !important;
      padding: 8px 16px !important;
    }
  }
`

const Point = styled.div<{ isFirst: boolean }>(
  ({ isFirst }) => `
  margin: 0 auto;
  position: relative;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #77778e;

  ${
    !isFirst &&
    `
    &:before {
      content: '';
      position: absolute;
      bottom: calc(100% + 8px);
      left: calc(50% - 1px);
      width: 2px;
      height: 28px;
      border-radius: 1px;
      background-color: #4a4a68;
    }
  `
  }
`
)

export default StatusLog
